import type { ReactNode } from 'react';
import { FiArrowRight } from 'react-icons/fi';
import type { EventTicket } from '../../api/events';
import { calculateCheckoutSummary } from '../../api/checkout';

interface OrderSummaryCardProps {
  tickets: EventTicket[];
  ticketCounts: Record<number, number>;
  onCheckout: () => void;
  checkoutDisabled?: boolean;
  /** Rendered in the floating group, directly under the checkout button. */
  floatingFooter?: ReactNode;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
}

export function OrderSummaryCard({
  tickets,
  ticketCounts,
  onCheckout,
  checkoutDisabled,
  floatingFooter,
}: OrderSummaryCardProps) {
  const totalTickets = Object.values(ticketCounts).reduce((sum, qty) => sum + qty, 0);
  const subtotal = tickets.reduce((sum, t) => {
    if (t.id === undefined) return sum;
    const qty = ticketCounts[t.id] ?? 0;
    return sum + t.price * qty;
  }, 0);
  const isEmpty = totalTickets === 0;
  // Fee math includes a flat processing fee even at $0 subtotal, which is only
  // meaningful once there's an actual cart — skip it entirely while empty.
  const summary = isEmpty
    ? { subtotal: 0, platformFee: 0, processingFee: 0, total: 0 }
    : calculateCheckoutSummary(subtotal);
  const isFree = subtotal === 0 && totalTickets > 0;

  return (
    <div className="rounded-3xl border border-white/8 bg-zinc-950 p-5 space-y-3">
      <div className="flex items-center justify-between text-[13px] text-zinc-400">
        <span>Subtotal</span>
        <span className="text-zinc-200">{formatCurrency(summary.subtotal)}</span>
      </div>
      <div className="flex items-center justify-between text-[13px] text-zinc-400">
        <span>Platform Fee (7%)</span>
        <span className="text-zinc-200">{formatCurrency(summary.platformFee)}</span>
      </div>
      <div className="flex items-center justify-between text-[13px] text-zinc-400">
        <span>Processing Fee</span>
        <span className="text-zinc-200">{formatCurrency(summary.processingFee)}</span>
      </div>
      <div className="h-px bg-white/8" />
      <div className="flex items-center justify-between text-[15px] font-semibold text-white">
        <span>Total</span>
        <span>{isFree ? 'Free' : formatCurrency(summary.total)}</span>
      </div>

      <div className="fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+7rem)] z-60 sm:bottom-[calc(env(safe-area-inset-bottom)+1rem)] px-4 sm:px-6 lg:px-8 sm:max-w-360 sm:mx-auto pointer-events-none">
        <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-8 lg:gap-10">
          <div className="hidden lg:block" aria-hidden="true" />
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={onCheckout}
              disabled={checkoutDisabled || isEmpty}
              className="pointer-events-auto w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95 text-[14px] font-semibold shadow-2xl shadow-black/30 ring-1 ring-black/10 transition-[filter] disabled:bg-zinc-700 disabled:text-zinc-400 disabled:shadow-none disabled:ring-0 disabled:cursor-not-allowed"
            >
              Proceed to Checkout
              <FiArrowRight size={15} />
            </button>
            {floatingFooter && (
              <div className="pointer-events-auto empty:hidden">{floatingFooter}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
